import React, { useRef, useEffect, useState } from 'react';
import { FormControl, Input, InputLabel, FormHelperText } from '@material-ui/core';
import classnames from 'classnames';
import styles from './TextInput.module.scss';
import CircleExclamation from '../icons/CircleExclamation';

// set by design
const SMALLEST_ERROR_WIDTH = 200;

type TextInputProps = {
    autoComplete?: string;
    autoFocus?: boolean;
    className?: string;
    classes?: any;
    color?: 'primary' | 'secondary';
    defaultValue?: any;
    disabled?: boolean;
    error?: boolean;
    errorMessage?: string;
    fullWidth?: boolean;
    helperText?: string;
    id?: string;
    inputRef?: any;
    label?: string;
    muiChildrenProps?: {
        InputProps?: any;
        InputLabelProps?: any;
        FormHelperTextProps?: any;
    };
    multiline?: boolean;
    name?: string;
    onBlur?: any;
    onChange?: any;
    onFocus?: any;
    placeholder?: string;
    required?: boolean;
    value?: any;
};

const TextInput: React.FC<TextInputProps> = ({
    autoComplete,
    autoFocus,
    classes,
    className,
    color,
    defaultValue,
    disabled,
    error,
    errorMessage,
    fullWidth,
    id,
    inputRef,
    label,
    muiChildrenProps,
    multiline,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required,
    value,
}) => {
    const [initialWidth, setInitialWidth] = useState<number>(0);
    const inputId = id ?? "text-input";
    const showHelperText = (error && errorMessage) || muiChildrenProps?.FormHelperTextProps?.children;
    const inputRootRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (inputRootRef.current) {
            const width = inputRootRef.current?.clientWidth;
            setInitialWidth(width);
        }
    }, []);
    function getFormHelperTextContent () {
        if (muiChildrenProps?.FormHelperTextProps?.children) {
            return muiChildrenProps.FormHelperTextProps.children;
        }
        let computedStyles;
        if (initialWidth) {
            const widthVal = initialWidth < 200 ? SMALLEST_ERROR_WIDTH : initialWidth;
            computedStyles = { maxWidth: widthVal};
        }
        return (
            <div className={styles.errorTextContainer} style={computedStyles}>
                <div className={styles.iconWrapper}><CircleExclamation /></div>
                <span className={styles.errorTextMessage}>{errorMessage}</span>
            </div>
        );
    }
    const inputLabelClasses = {
        ...muiChildrenProps?.InputLabelProps?.classes,
        root: classnames(styles.inputLabelRoot, muiChildrenProps?.InputLabelProps?.classes?.root),
        shrink: classnames(styles.inputLabelShrink, muiChildrenProps?.InputLabelProps?.classes?.shrink),
    };
    const inputClasses = {
        ...muiChildrenProps?.InputProps?.classes,
        root: classnames(styles.inputRoot, muiChildrenProps?.InputProps?.classes?.root),
        disabled: classnames(styles.inputDisabled, muiChildrenProps?.InputProps?.classes?.disabled),
    };
    let computedInputStyles;
    if (initialWidth && initialWidth < 200) {
        computedInputStyles = { maxWidth: initialWidth };
    }
    return (
        <FormControl
            error={error}
            disabled={disabled}
            className={classnames(styles.textInput, classes?.root, className)}
            required={required}
            fullWidth={fullWidth}
            color={color}
        >
            {label && (
                <InputLabel htmlFor={inputId} id={`${inputId}__label`} {...(muiChildrenProps?.InputLabelProps)} classes={inputLabelClasses} shrink>
                    {label}
                </InputLabel>
            )}
            <Input
                id={id ?? "text-input"}
                ref={inputRootRef}
                classes={inputClasses}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                defaultValue={defaultValue}
                fullWidth={fullWidth}
                inputRef={inputRef}
                inputProps={muiChildrenProps?.InputProps}
                multiline={multiline}
                value={value}
                placeholder={placeholder}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                disableUnderline
                style={computedInputStyles}
            />
            { showHelperText && (
                <FormHelperText id={`${inputId}__helper-text`} component="div" {...(muiChildrenProps?.FormHelperTextProps)}>
                    { getFormHelperTextContent() }
                </FormHelperText>
            )}
        </FormControl>
    );
};



export default TextInput;